<template>
  <div class="p-2 tracker-health-con">
    <div class="d-flex justify-content-center align-items-center flex-column p-6">
      <div class="d-inline-block">
        <p><b>You can add the tracker combination by uploading the tracker spreadsheet</b></p>
      </div>
    </div>
    <div class="table-groups">
      <div
        class="table-group"
        v-for="(trackerGroup, type) in groupedTrackerItems"
        :key="type"
      >
        <h3>Combination: {{ type }}</h3>
        <b-table
          v-if="trackerGroup.length >= 1"
          class="card-table trackers-list"
          hover
          selectable
          select-mode="single"
          :fields="headers"
          :items="trackerGroup"
          data-test="trackers-list"
          :ref="getTableRef(type)"
          @row-selected="onRowSelected"
          :tbody-tr-class="highlightedRows"
        >
          <template v-slot:table-colgroup="scope">
            <col
              v-for="field in scope.fields"
              :key="field.key"
              :style="`width: ${field.width ? field.width : 'auto'}`"
            >
          </template>
          <template v-slot:cell(selected)="data">
            <div class="selected-checkbox">
              <b-form-checkbox
                v-model="data.item.selected"
                :value="true"
                :unchecked-value="false"
                @input="updateSelected($event, data.item)"
              />
            </div>
          </template>
          <template v-slot:[referenceColumnCellSelector]="data">
            <div
              class="reference-column"
              v-b-popover.hover.top="data.value"
            >
              {{ data.value }}
            </div>
          </template>
          <template v-slot:cell(contentItemId)="data">
            <div class="content-item">
              <div
                v-if="data.item.uuid === selectedRow.uuid"
                class="d-flex align-items-center"
              >
                <b-form-select
                  :value="data.item.contentItemId"
                  :options="contentItemOptions"
                  data-test="content-item-selection"
                  class="content-item-selection"
                  @change="onContentItemSelected($event, data.item, type)"
                />
                <div
                  v-if="contentItemsValidation(data.item, type)"
                  @click="$bvModal.show('duplicate-trackers-match-modal')"
                >
                  <div
                    class="icon"
                    title="this content item is already in use"
                  >
                    <i class="fas fa-exclamation-triangle warning m-auto p-2" />
                  </div>
                </div>
              </div>
              <div
                v-else-if="data.item.contentItemId"
                class="d-flex align-items-center"
              >
                <span :title="getContentItemTextFromId(data.item.contentItemId)">
                  {{ getContentItemTextFromId(data.item.contentItemId) }}
                </span>
                <div
                  v-if="contentItemsValidation(data.item, type)"
                  @click="$bvModal.show('duplicate-trackers-match-modal')"
                >
                  <div
                    class="icon"
                    title="this content item is already in use"
                  >
                    <i class="fas fa-exclamation-triangle warning m-auto p-2" />
                  </div>
                </div>
              </div>
              <div
                v-else
                class="d-flex align-items-center"
              >
                <span
                  class="select-content-item-text"
                  data-test="select-content-item-text"
                >
                  click here to change content item
                </span>
              </div>
            </div>
          </template>

          <template v-slot:cell(impressionTracker)="data">
            <status-btn
              v-if="data.item.impressionTracker"
              tracker-type="impressionTracker"
              :trackers-info="[...data.item.impressionTracker]"
              :scope="isImportingPlacementLevelTrackers ? 'FEED' : 'CONTENT_ITEM'"
            />
          </template>
          <template v-slot:cell(clickTracker)="data">
            <status-btn
              v-if="data.item.clickTracker"
              tracker-type="clickTracker"
              :key="data.item.contentItemId"
              :trackers-info="[{ ...data.item.clickTracker, landingPage: data.item.landingPage, contentItem: getContentItemFromId(data.item.contentItemId) }]"
              :scope="isImportingPlacementLevelTrackers ? 'FEED' : 'CONTENT_ITEM'"
            />
          </template>
          <template v-slot:cell(postClickTracker)="data">
            <status-btn
              v-if="data.item.postClickTracker"
              tracker-type="postClickTracker"
              :trackers-info="[...data.item.postClickTracker]"
              :scope="isImportingPlacementLevelTrackers ? 'FEED' : 'CONTENT_ITEM'"
            />
          </template>
          <template v-slot:cell(thirdPartyTags)="data">
            <status-btn
              v-if="data.item.thirdPartyTags.length"
              tracker-type="thirdPartyTags"
              :trackers-info="[...data.item.thirdPartyTags]"
              :scope="isImportingPlacementLevelTrackers ? 'FEED' : 'CONTENT_ITEM'"
              :display-count="false"
              :disabled="false"
            />
          </template>
        </b-table>
      </div>
    </div>
    <base-pagination
      v-if="totalItems > perPageCount"
      class="pt-3 pagination-edit"
      :items-count="totalItems"
      :current-page.sync="currentPage"
      :per-page-selected="perPageCount"
      :selected-row-count="selectedRowCount"
      items-name="Items"
      :hide-page-select="true"
    />
    <b-modal
      id="duplicate-trackers-match-modal"
      hide-header-close
      centered
    >
      <template #modal-title>
        <p class="mb-0 font-weight-bold">
          <span class="icon mr-2">
            <i class="fas fa-exclamation-triangle warning" />
          </span>
          WARNING: Duplicate Content Items
        </p>
      </template>
      <div>
        <p
          class="mb-0"
        >
          Tracker can't be assigned to multiple items
        </p>
      </div>
      <template #modal-footer>
        <b-button
          size="md"
          variant="primary"
          @click="$bvModal.hide('duplicate-trackers-match-modal')"
        >
          OK
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  mapState,
  mapActions,
  mapGetters,
  mapMutations,
} from 'vuex';
import * as trackersTypes from '@/modules/trackers/store/types';

import textHelper from '@/core/mixins/textHelper';

import StatusBtn from './trackerHealth/StatusBtn.vue';
import BasePagination from '@/components/BasePagination.vue';

export default {
  name: 'TrackerHealth',
  mixins: [textHelper],
  components: {
    BasePagination,
    StatusBtn,
  },
  props: {
    referenceColumn: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      selectedRow: {},
      currentPage: 1,
      perPageCount: 20,
    };
  },
  computed: {
    ...mapState('trackers', [
      'isImportingPlacementLevelTrackers',
      'selectedTrackersForValidation',
    ]),
    ...mapState('campaigns', [
      'campaign',
    ]),
    ...mapState('content', [
      'contentItems',
    ]),
    ...mapGetters('trackers', [
      trackersTypes.GET_ALL_COMBINATIONS,
    ]),
    referenceColumnCellSelector() {
      return `cell(${this.referenceColumn})`;
    },
    trackerItems() {
      return this.selectedTrackersForValidation;
    },
    selectedRowCount() {
      return this.selectedTrackersForValidation.filter((item) => item.selected).length;
    },
    totalItems() {
      return this.trackerItems.length;
    },
    orderedTrackerItems() {
      const { trackerItems } = this;
      return trackerItems.sort((a) => this.getCombinationTextFromItem(a));
    },
    fullGroupedTrackerItems() {
      const { orderedTrackerItems } = this;
      if (orderedTrackerItems.length < 1) return;
      return this.separateTrackersIntoGroups(orderedTrackerItems);
    },
    groupedTrackerItems() {
      const { orderedTrackerItems } = this;
      if (orderedTrackerItems.length < 1) return;
      const startingIndex = (this.currentPage - 1) * this.perPageCount;
      const endingIndex = this.currentPage * this.perPageCount;
      const reducedTrackerItems = orderedTrackerItems.slice(startingIndex, endingIndex);
      return this.separateTrackersIntoGroups(reducedTrackerItems);
    },
    contentItemOptions() {
      return this.contentItems.map((item) => ({ text: item.title, value: item.id }));
    },
    headers() {
      const { referenceColumn } = this;
      const headers = [
        {
          key: 'selected',
          label: 'selected',
          width: '30px',
        },
        {
          key: referenceColumn,
          label: referenceColumn,
          width: '200px',
        },
        {
          key: 'impressionTracker',
          label: 'Impression Tag',
          width: '120px',
        },
        {
          key: 'clickTracker',
          label: 'Click Tag',
          width: '120px',
        },
        {
          key: 'postClickTracker',
          label: 'Post Click',
          width: '120px',
        },
        {
          key: 'thirdPartyTags',
          label: '3RD Party Tags',
          width: '120px',
        },
      ];
      const contentItemCol = {
        key: 'contentItemId',
        label: 'Content Item Match',
        width: '300px',
      };
      if (!this.isImportingPlacementLevelTrackers) headers.splice(2, 0, contentItemCol);
      return headers;
    },
  },
  async created() {
    this.$emit('disable-next', true);
    const { referenceColumn } = this;
    await this.createItems({ referenceColumn });
    this.checkAllValidationResults();
    if (this.isImportingPlacementLevelTrackers) {
      this.trackerItems.forEach((trackerItem) => {
        trackerItem.feedId = this.campaign.feedId;
      });
    }
    this.updateNextBtn();
  },
  methods: {
    ...mapActions('trackers', {
      createItems: trackersTypes.CREATE_TRACKERS_FOR_VALIDATION,
    }),
    ...mapMutations('trackers', {
      updateTrackerItem: trackersTypes.UPDATE_TRACKERS_FOR_VALIDATION_BY_ITEM,
    }),
    checkAllValidationResults() {
      const allCompleted = this.trackerItems.every((trackerItem) => {
        const {
          impressionTracker, clickTracker, imageTrackers, scriptTrackers,
        } = trackerItem;
        return (!impressionTracker || impressionTracker.status === 'COMPLETE')
          && (!clickTracker || clickTracker.status === 'COMPLETE')
          && imageTrackers.every((tracker) => tracker.status === 'COMPLETE')
          && scriptTrackers.every((tracker) => tracker.status === 'COMPLETE');
      });
      if (!allCompleted) {
        const { referenceColumn } = this;
        setTimeout(async () => {
          await this.createItems({ referenceColumn });
          this.checkAllValidationResults();
        }, 1000);
      }
    },
    hasCombination(item) {
      const hasCreativeIndex = item.creativeIndex != null;
      const hasPublisherIndex = item.publisherIndex != null;
      const hasGeoIndex = item.geoIndex != null;
      return hasCreativeIndex || hasPublisherIndex || hasGeoIndex;
    },
    separateTrackersIntoGroups(trackers) {
      if (!trackers || !Array.isArray(trackers)) return {};
      const groups = {};
      trackers.forEach((item) => {
        const combination = this.getCombinationTextFromItem(item);
        if (!groups[combination]) groups[combination] = [item];
        else groups[combination].push(item);
      });
      return groups;
    },
    // Table Row Selection
    getTableRef(type) {
      return `${type.replace(/ /g, '_')}-table`;
    },
    highlightedRows(item, type) {
      if (!item || type !== 'row') return;
      const itemCombination = this.getCombinationTextFromItem(item);
      if (!item.selected) return;
      if (this.contentItemsValidation(item, itemCombination)) return 'alert-danger';
      return (item.contentItemId) ? '' : 'alert-warning';
    },
    onRowSelected(rowsSelected) {
      if (rowsSelected == null || rowsSelected.length === 0) return;
      const row = rowsSelected[0];
      if (!row.uuid) return;
      const refsList = Object.keys(this.groupedTrackerItems)
        .filter((item) => item !== this.getCombinationTextFromItem(row))
        .map((item) => this.getTableRef(item));
      this.clearAllSelectedTableRows(refsList);
      this.selectedRow = row;
    },
    clearAllSelectedTableRows(refs) {
      refs.forEach((ref) => {
        this.$refs[ref][0].clearSelected();
      });
    },
    // Content Item Methods
    getContentItemTextFromId(id) {
      const contentItem = this.contentItemOptions.find((item) => item.value === id);
      return contentItem ? contentItem.text : 'click here to change content item';
    },
    hasDuplicateContentItems(type) {
      const contentItemsRecord = {};
      if (typeof this.fullGroupedTrackerItems !== 'object' || Array.isArray(this.fullGroupedTrackerItems) || this.fullGroupedTrackerItems === null) return false;
      Object.keys(this.fullGroupedTrackerItems).forEach((key) => {
        const trackerItems = this.fullGroupedTrackerItems[key].filter((item) => item.selected);
        trackerItems.forEach((tracker) => {
          if (tracker.contentItemId !== null) {
            if (!contentItemsRecord[key]) contentItemsRecord[key] = {};
            if (!contentItemsRecord[key][tracker.contentItemId]) contentItemsRecord[key][tracker.contentItemId] = 1;
            else contentItemsRecord[key][tracker.contentItemId] += 1;
          }
        });
      });
      return !type
        ? Object.values(contentItemsRecord).some((group) => this.doesGroupHasDuplicates(group))
        : this.doesGroupHasDuplicates(contentItemsRecord[type]);
    },
    doesGroupHasDuplicates(group) {
      return Object.values(group).length > 0 && Object.values(group).some((val) => val > 1);
    },
    contentItemsValidation(item, type) {
      const trackerItems = this.fullGroupedTrackerItems[type].filter((tracker) => tracker.selected);
      const filteredTrackerItems = trackerItems.filter((tracker) => tracker.uuid !== item.uuid && tracker.contentItemId);
      return filteredTrackerItems.some((tracker) => tracker.contentItemId === item.contentItemId);
    },
    getContentItemFromId(id) {
      const contentItem = this.contentItems.find((item) => item.id === id);
      return contentItem;
    },
    updateSelected(val, item) {
      const trackerItem = { ...item };
      trackerItem.selected = val;
      this.updateTrackerItem(trackerItem);
      this.updateNextBtn();
    },
    isEveryContentItemSet() {
      if (!this.isImportingPlacementLevelTrackers) {
        return (this.trackerItems.filter((item) => item.selected).every((item) => item.contentItemId));
      }
    },
    onContentItemSelected(contentItemId, item, type) {
      const trackerItem = { ...item };
      trackerItem.contentItemId = contentItemId;
      this.updateTrackerItem(trackerItem);
      this.updateNextBtn(type);
    },
    getCombinationTextFromItem(item) {
      if (!item) return;
      const combinations = this[trackersTypes.GET_ALL_COMBINATIONS];
      const scope = this;
      function getTextFromIndex(type, index) {
        if (index !== null) return combinations[type][index].text;
        return `All ${scope.capitalizeFirstLetter(type)}`;
      }
      return `${getTextFromIndex('creatives', item?.creativeIndex)} - ${getTextFromIndex('publishers', item?.publisherIndex)} - ${getTextFromIndex('geos', item?.geoIndex)}`;
    },
    updateNextBtn(type) {
      if (this.isImportingPlacementLevelTrackers) this.$emit('disable-next', false);
      else this.$emit('disable-next', !this.isEveryContentItemSet() || this.hasDuplicateContentItems(type));
    },
  },
};
</script>

<style lang="scss">
.tracker-health-con {
  height: 60vh;
}

.table-groups {
  width: 100%;
  max-height: calc(100% - 93px);
  overflow-x: auto;
  .table-group {
    h3 {
      font-size: 14px;
    }
  }
}
.card-table tr td:first-child {
  min-width: 30px;
}
.pagination-edit {
  margin: 10px 105px 0 105px;
}
.selected-checkbox {
  width: 100px;
}
.reference-column {
    width: 450px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}
.content-item {
  width: 300px;
  div {
    span {
      width: calc(100% - 62px);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .content-item-selection {
    width: calc(100% - 62px);
  }
}

$radio-size: 1.2rem;

.import-trackers {
  &__radio {
    .custom-control-label {
      &::before {
        width: $radio-size;
        height: $radio-size;
      }

      &::after {
        width: $radio-size;
        height: $radio-size;
      }
    }
  }
}
</style>
